<script lang="ts">
  import { onMount } from "svelte";
  import CustomFieldEntityDropdown from "../../components/Business/CustomFieldEntityDropdown.svelte";
  import CustomFieldTypeDropdown from "../../components/Business/CustomFieldTypeDropdown.svelte";
  import CustomListSchemaDropdown from "../../components/Form/Fields/Dropdown/CustomListSchemaDropdown.svelte";
  import CustomListValueDropdown from "../../components/Form/Fields/Dropdown/CustomListValueDropdown.svelte";
  import Input from "../../components/Form/Fields/Input.svelte";
  import NumberInput from "../../components/Form/Fields/NumberInput.svelte";
  import Switch from "../../components/Form/Fields/Switch.svelte";
  import FormField from "../../components/Form/FormField.svelte";
  import GlobalErrors from "../../components/GlobalErrors.svelte";
  import Table from "../../components/Table/Table.svelte";
  import FormLayout from "../../layout/FormLayout.svelte";
  import { CustomFieldSchemaService } from "../../services/custom-field-schema.service";
  import type { IDropdownItem } from "../../services/DTO/dropdown-item";
  import { customFieldSchemaDtoFields } from "../../utils/TableFields/custom-field-schema-dto.fields";
  import {
    editing,
    init,
    loading,
    loadEntityValues,
    onCreate,
    onDelete,
    onEdit,
    record,
    records,
    save,
    selectedEntity,
    selectedTable,
    selectedFieldType,
  } from "./settings-custom-fields.store";

  let canSubmit = true;

  $: isNew = $record != null && $record.oid === 0;
  $: canCreate = $selectedEntity != null && $selectedEntity != 0;
  $: isDropdownField =
    $selectedFieldType != null && $selectedFieldType.code === "DROPDOWNXX";
  $: isDateField =
    $selectedFieldType != null &&
    ($selectedFieldType.code === "DATEXXXXXX" ||
      $selectedFieldType.code === "DATETIMEXX");

  function onEntitySelected(item: IDropdownItem) {
    if (!!item) {
      selectedEntity.set(item.oid);
      loadEntityValues(item.oid);
    } else {
      selectedEntity.set(0);
      records.set([]);
    }
  }

  async function onFieldTypeSelected(item: IDropdownItem) {
    if (!!item) {
      let _fieldType = await CustomFieldSchemaService.fetchCustomFieldType(
        item.oid
      ).toPromise();
      selectedFieldType.set(_fieldType.payload);
      $record.defaultValue = "";
    } else {
      selectedFieldType.set(null);
      $record.defaultValue = "";
    }
  }

  function onTableSelected(item: IDropdownItem) {
    if (!!item) {
      selectedTable.set(item.oid);
    } else {
      selectedTable.set(0);
    }
  }

  async function onSubmit() {
    await save();
    editing.set(false);
    record.set(null);
  }

  function onCancel() {
    editing.set(false);
    record.set(null);
  }

  onMount(init);
</script>

{#if $editing === false}
  <nav class="level">
    <div class="level-left">
      <div class="level-item">
        <p class="subtitle is-5">Entità</p>
      </div>

      <div class="level-item">
        <div class="field">
          <CustomFieldEntityDropdown
            name=""
            filterVisible={false}
            value={$selectedEntity}
            on:select={(x) => onEntitySelected(x.detail)}
          />
        </div>
      </div>
    </div>
  </nav>

  <GlobalErrors errors={[]} />

  <div style="padding-bottom: 2rem;">
    <Table
      loading={$loading}
      fields={customFieldSchemaDtoFields}
      records={$records}
      pagination={null}
      createEnabled={canCreate}
      editEnabled={true}
      deleteEnabled={true}
      on:create={(_) => onCreate()}
      on:edit={(e) => onEdit(e.detail)}
      on:delete={(e) => onDelete(e.detail)}
    />
  </div>
{:else if $record != null}
  <FormLayout on:submit={onSubmit} on:cancel={onCancel} {canSubmit} {isNew}>
    <div class="columns">
      <div class="column">
        <FormField>
          <Input
            name="Nome Campo"
            placeholder=""
            bind:value={$record.fieldName}
            required={true}
            maxlength={40}
          />
        </FormField>
      </div>
      <div class="column">
        <FormField>
          <CustomFieldTypeDropdown
            name="Tipo"
            placeholder=""
            bind:value={$record.fieldTypeId}
            on:select={(x) => onFieldTypeSelected(x.detail)}
            required={true}
          />
        </FormField>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <FormField>
          <Input
            name="Label Campo"
            placeholder=""
            bind:value={$record.fieldLabel}
            required={true}
            maxlength={200}
          />
        </FormField>
      </div>
      <div class="column">
        {#if isDropdownField}
          <CustomListSchemaDropdown
            name="Tabella"
            filterVisible={true}
            bind:value={$record.listSchemaId}
            on:select={(x) => onTableSelected(x.detail)}
          />
        {/if}
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <FormField>
          <Input
            name="Descrizione"
            placeholder=""
            bind:value={$record.description}
            required={false}
            maxlength={200}
          />
        </FormField>
      </div>

      <div class="column">
        <FormField>
          {#if isDropdownField}
            <CustomListValueDropdown
              name="Valore di default"
              filterVisible={true}
              schemaId={$record.listSchemaId}
              value={parseInt($record.defaultValue)}
              on:select={(x) =>
                ($record.defaultValue = x.detail.oid.toString())}
            />
          {:else if isDateField}
            <Input
              name="Valore di default"
              placeholder=""
              bind:value={$record.defaultValue}
              required={false}
              disabled={true}
              maxlength={200}
            />
          {:else}
            <Input
              name="Valore di default"
              placeholder=""
              bind:value={$record.defaultValue}
              required={false}
              maxlength={200}
            />
          {/if}
        </FormField>
      </div>
    </div>

    <div class="columns">
      <div class="column is-2">
        <FormField>
          <Switch name="Visibile nelle liste" bind:value={$record.listable} />
        </FormField>
      </div>
      <div class="column is-6">
        <FormField>
          <NumberInput
            name="Ordine di visualizzazione nelle liste"
            required={true}
            bind:value={$record.listDisplayOrder}
          />
        </FormField>
      </div>

      <div class="column is-4" />
    </div>

    <div class="columns">
      <div class="column is-2">
        <FormField>
          <Switch name="Visibile del dettaglio" bind:value={$record.visible} />
        </FormField>
      </div>

      <div class="column is-2">
        <FormField>
          <Switch name="Editabile" bind:value={$record.editable} />
        </FormField>
      </div>

      <div class="column is-4">
        <FormField>
          <NumberInput
            name="Riga"
            required={true}
            bind:value={$record.detailDisplayRow}
          />
        </FormField>
      </div>

      <div class="column is-4">
        <FormField>
          <NumberInput
            name="Colonna"
            required={true}
            bind:value={$record.detailDisplayCol}
          />
        </FormField>
      </div>
    </div>
  </FormLayout>
{/if}
